import React, { useState, useEffect } from 'react';
import { X, Upload, Image as ImageIcon } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { categoryApi, unitApi, uploadApi, skuApi } from '../../api';
import ProductSpecEditor from './ProductSpecEditor';

const ProductDialog = ({ product, onClose, onSubmit, onSaved }) => {
  const { language } = useApp();

  const [formData, setFormData] = useState({
    name:          '',
    name_en:       '',
    name_km:       '',
    category_id:   '',
    unit_id:       '',
    sale_price:    '',
    vip_price:     '',
    cost_price:    '',
    stock:         0,
    min_stock:     0,
    image:         '',
    barcode:       '',
    description:   '',
    show_in_pos:   true,
    show_in_h5:    true,
    box_quantity:  '',
    box_price:     '',
    vip_box_price: '',
    // 促销价：手动开关控制，不设时间范围。最终结算价取"售价/VIP价/促销价"最低的那个
    promo_price:     '',
    promo_box_price: '',
    is_promo_active: false,
    sort_order:    10,
    status:        'active',
  });

  const [categories,      setCategories]      = useState([]);
  const [parentCategoryId, setParentCategoryId] = useState('');
  const [units,           setUnits]           = useState([]);
  const [imageFile,       setImageFile]       = useState(null);
  const [imagePreview,    setImagePreview]    = useState('');
  const [uploading,       setUploading]       = useState(false);
  const [loading,         setLoading]         = useState(false);
  const [showUrlInput,    setShowUrlInput]    = useState(false);
  const [imageUrlInput,   setImageUrlInput]   = useState('');
  const [urlError,        setUrlError]        = useState(false);

  const [specs, setSpecs] = useState([]);
  const [skus,  setSkus]  = useState([]);

  // 主图放大预览
  const [zoomImage, setZoomImage] = useState(null); // { src } | null

  const boxEnabled = formData.box_quantity !== '' && parseInt(formData.box_quantity) > 0;
  const skuStockTotal = skus.reduce((sum, sku) => sum + (parseInt(sku.stock) || 0), 0);

  const currentChildren = parentCategoryId
    ? (categories.find(p => p.id === parseInt(parentCategoryId))?.children || [])
    : [];

  useEffect(() => {
    loadCategories();
    loadUnits();
    if (product) {
      setFormData({
        name:          product.name          || '',
        name_en:       product.name_en       || '',
        name_km:       product.name_km       || '',
        category_id:   product.category_id   || '',
        unit_id:       product.unit_id       || '',
        sale_price:    product.sale_price    || '',
        vip_price:     product.vip_price     || '',
        cost_price:    product.cost_price    || '',
        stock:         product.stock         || 0,
        min_stock:     product.min_stock     || 0,
        image:         product.image         || '',
        barcode:       product.barcode       || '',
        description:   product.description   || '',
        show_in_pos:   product.show_in_pos !== false && product.show_in_pos !== 0,
        show_in_h5:    product.show_in_h5  !== false && product.show_in_h5  !== 0,
        box_quantity:  product.box_quantity  || '',
        box_price:     product.box_price     || '',
        vip_box_price: product.vip_box_price || '',
        promo_price:     product.promo_price     || '',
        promo_box_price: product.promo_box_price || '',
        is_promo_active: !!product.is_promo_active,
        sort_order:    product.sort_order    || 0,
        status:        product.status        || 'active',
      });
      if (product.image) setImagePreview(product.image);

      skuApi.getProductSpecs(product.id).then(res => {
        if (res.success) {
          const loadedSpecs = (res.data.specs || []).map(s => ({ ...s, values: s.values || [] }));
          const loadedSkus = (res.data.skus || []).map(sk => ({
            ...sk,
            spec_values: (sk.spec_values || []).map(sv => {
              const specIndex = loadedSpecs.findIndex(s => s.id === sv.spec_id);
              const valueIndex = specIndex >= 0
                ? loadedSpecs[specIndex].values.findIndex(v => v.id === sv.spec_value_id)
                : -1;
              return { spec_index: specIndex, value_index: valueIndex };
            })
          }));
          setSpecs(loadedSpecs);
          setSkus(loadedSkus);
        }
      }).catch(() => {});
    }
  }, [product]);

  useEffect(() => {
    if (product && product.category_id && categories.length > 0) {
      let parent = categories.find(p => p.id === product.category_id);
      if (parent) {
        setParentCategoryId(String(parent.id));
      } else {
        parent = categories.find(p => (p.children || []).some(c => c.id === product.category_id));
        if (parent) setParentCategoryId(String(parent.id));
      }
    }
  }, [product, categories]);

  const loadCategories = async () => {
    try {
      const res = await categoryApi.getAll ? await categoryApi.getAll() : await categoryApi.getList();
      if (res.success) setCategories(res.data || []);
    } catch (err) { console.error('加载分类失败:', err); }
  };

  const loadUnits = async () => {
    try {
      const res = await unitApi.getList();
      if (res.success) setUnits(res.data || []);
    } catch (err) { console.error('加载单位失败:', err); }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setShowUrlInput(false);
    setImageUrlInput('');
    setUrlError(false);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleUrlConfirm = () => {
    const url = imageUrlInput.trim();
    if (!url) return;
    setUrlError(false);
    setImageFile(null);
    setImagePreview(url);
    setFormData(prev => ({ ...prev, image: url }));
  };

  const handleRemoveImage = () => {
    setImagePreview('');
    setImageFile(null);
    setImageUrlInput('');
    setUrlError(false);
    setFormData(prev => ({ ...prev, image: '' }));
  };

  const handleZoomOpen = () => {
    if (!imagePreview) return;
    setZoomImage({ src: imagePreview });
  };

  const validateBox = () => {
    if ((formData.box_price || formData.vip_box_price || formData.promo_box_price) && !boxEnabled) {
      alert('设置箱价时必须先填写箱容（每箱数量）');
      return false;
    }
    return true;
  };

  const buildSubmitData = (imagePath) => ({
    name:          formData.name,
    name_en:       formData.name_en,
    name_km:       formData.name_km,
    // 子类没选的话，用大类本身的 id——之前这里只取了 formData.category_id（子类），
    // 子类为空时直接变成 null（未分类），和下拉框提示文案"不选则用大类本身"说的不一致
    category_id:   formData.category_id || parentCategoryId || null,
    unit_id:       formData.unit_id || null,
    barcode:       formData.barcode || null,
    description:   formData.description || null,
    image:         imagePath,
    sale_price:    parseFloat(formData.sale_price) || 0,
    vip_price:     formData.vip_price  ? parseFloat(formData.vip_price)  : null,
    cost_price:    formData.cost_price ? parseFloat(formData.cost_price) : null,
    stock:         parseInt(formData.stock)     || 0,
    min_stock:     parseInt(formData.min_stock) || 0,
    box_quantity:  boxEnabled ? parseInt(formData.box_quantity) : null,
    box_price:     boxEnabled && formData.box_price     ? parseFloat(formData.box_price)     : null,
    vip_box_price: boxEnabled && formData.vip_box_price ? parseFloat(formData.vip_box_price) : null,
    promo_price:     formData.promo_price ? parseFloat(formData.promo_price) : null,
    promo_box_price: boxEnabled && formData.promo_box_price ? parseFloat(formData.promo_box_price) : null,
    is_promo_active: !!formData.is_promo_active,
    sort_order:    parseInt(formData.sort_order) || 0,
    status:        formData.status,
    show_in_pos:   formData.show_in_pos,
    show_in_h5:    formData.show_in_h5,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name)       { alert('请输入商品名称'); return; }
    if (!formData.sale_price) { alert('请输入售价');   return; }
    if (!formData.cost_price) { alert('请输入成本价'); return; }
    if (!formData.barcode)    { alert('请输入条形码'); return; }
    if (!validateBox()) return;

    setLoading(true);
    let imagePath = formData.image;

    if (imageFile) {
      setUploading(true);
      try {
        const uploadRes = await uploadApi.uploadProductImage(imageFile);
        if (uploadRes?.data?.url) {
          imagePath = uploadRes.data.url;
        } else {
          alert('图片上传失败');
          setUploading(false); setLoading(false); return;
        }
      } catch (err) {
        alert('图片上传失败: ' + (err.message || '未知错误'));
        setUploading(false); setLoading(false); return;
      }
      setUploading(false);
    }

    try {
      const res = await onSubmit(buildSubmitData(imagePath));
      if (!res || !res.success) {
        alert(res?.message || '商品保存失败');
        setLoading(false);
        return;
      }

      const productId = product ? product.id : (res.data?.id);

      if (productId) {
        const hasSpecs = specs.length > 0;
        if (hasSpecs || product) {
          try {
            const specRes = await skuApi.saveProductSpecs(productId, { specs, skus });
            if (!specRes.success) {
              alert('商品已保存，但规格保存失败：' + (specRes.message || '未知错误'));
            }
          } catch (err) {
            alert('商品已保存，但规格保存失败：' + (err.message || '未知错误'));
          }
        }
      }

      alert(product ? '商品更新成功！' : '商品添加成功！');
      onSaved && onSaved();
    } catch (err) {
      alert(err.message || '保存失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent";
  const labelClass = "block text-sm font-medium text-gray-700 mb-1";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">

        <div className="flex items-center justify-between p-6 border-b flex-shrink-0">
          <h2 className="text-xl font-bold text-gray-800">
            {product ? '编辑商品' : '新增商品'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">

          <div>
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">基本信息</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className={labelClass}>商品名称 <span className="text-red-500">*</span></label>
                <input type="text" className={inputClass} value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  placeholder="中文名称" required />
              </div>
              <div>
                <label className={labelClass}>English Name</label>
                <input type="text" className={inputClass} value={formData.name_en}
                  onChange={e => setFormData({ ...formData, name_en: e.target.value })}
                  placeholder="English" />
              </div>
              <div>
                <label className={labelClass}>ឈ្មោះខ្មែរ</label>
                <input type="text" className={inputClass} value={formData.name_km}
                  onChange={e => setFormData({ ...formData, name_km: e.target.value })}
                  placeholder="ភាសាខ្មែរ" />
              </div>
            </div>
          </div>

          {/* 分类和单位 */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className={labelClass}>大类</label>
              <select className={inputClass} value={parentCategoryId}
                onChange={e => {
                  setParentCategoryId(e.target.value);
                  setFormData({ ...formData, category_id: '' });
                }}>
                <option value="">请选择大类</option>
                {categories.map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>子类</label>
              <select className={inputClass} value={formData.category_id}
                disabled={!parentCategoryId}
                onChange={e => setFormData({ ...formData, category_id: e.target.value })}>
                <option value="">
                  {!parentCategoryId
                    ? '先选择大类'
                    : currentChildren.length > 0
                      ? '不选则用大类本身'
                      : '该大类无子类'}
                </option>
                {currentChildren.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>单位</label>
              <select className={inputClass} value={formData.unit_id}
                onChange={e => setFormData({ ...formData, unit_id: e.target.value })}>
                <option value="">请选择单位</option>
                {units.map(u => (
                  <option key={u.id} value={u.id}>{u.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">价格设置</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className={labelClass}>售价 (USD) <span className="text-red-500">*</span></label>
                <input type="number" step="0.01" min="0" className={inputClass}
                  value={formData.sale_price}
                  onChange={e => setFormData({ ...formData, sale_price: e.target.value })}
                  placeholder="0.00" required />
              </div>
              <div>
                <label className={labelClass}>VIP价 (USD)</label>
                <input type="number" step="0.01" min="0" className={inputClass}
                  value={formData.vip_price}
                  onChange={e => setFormData({ ...formData, vip_price: e.target.value })}
                  placeholder="留空则无VIP价" />
              </div>
              <div>
                <label className={labelClass}>成本价 (USD) <span className="text-red-500">*</span></label>
                <input type="number" step="0.01" min="0" className={inputClass}
                  value={formData.cost_price}
                  onChange={e => setFormData({ ...formData, cost_price: e.target.value })}
                  placeholder="0.00" required />
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">库存设置</h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className={labelClass}>
                  库存数量
                  {specs.length > 0 && <span className="text-xs text-blue-500 ml-1">（自动计算）</span>}
                </label>
                <input
                  type="number" min="0"
                  className={`${inputClass} ${specs.length > 0 ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : ''}`}
                  value={specs.length > 0 ? skuStockTotal : formData.stock}
                  onChange={e => setFormData({ ...formData, stock: e.target.value })}
                  disabled={specs.length > 0}
                />
              </div>
              <div>
                <label className={labelClass}>最低库存预警</label>
                <input type="number" min="0" className={inputClass}
                  value={formData.min_stock}
                  onChange={e => setFormData({ ...formData, min_stock: e.target.value })} />
              </div>
              <div>
                <label className={labelClass}>条形码 <span className="text-red-500">*</span></label>
                <input type="text" className={inputClass}
                  value={formData.barcode}
                  onChange={e => setFormData({ ...formData, barcode: e.target.value })}
                  placeholder="必填" required />
              </div>
            </div>
            {specs.length > 0 && (
              <p className="text-xs text-blue-600 mt-2">
                ℹ️ 该商品已设多规格，此处库存数量为所有规格（SKU）库存之和，自动计算，无需手动填写；如需调整库存，请在下方各规格行分别设置
              </p>
            )}
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
              箱装设置 <span className="text-xs text-gray-400 normal-case">（填了箱容才启用整箱销售）</span>
            </h3>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className={labelClass}>每箱数量（箱容）</label>
                <input type="number" min="1" className={inputClass}
                  value={formData.box_quantity}
                  onChange={e => setFormData({ ...formData, box_quantity: e.target.value })}
                  placeholder="如 24" />
              </div>
              <div>
                <label className={labelClass}>箱价 (USD)</label>
                <input type="number" step="0.01" min="0" className={inputClass}
                  value={formData.box_price}
                  disabled={!boxEnabled}
                  onChange={e => setFormData({ ...formData, box_price: e.target.value })}
                  placeholder={boxEnabled ? '整箱价格' : '先填箱容'} />
              </div>
              <div>
                <label className={labelClass}>VIP箱价 (USD)</label>
                <input type="number" step="0.01" min="0" className={inputClass}
                  value={formData.vip_box_price}
                  disabled={!boxEnabled}
                  onChange={e => setFormData({ ...formData, vip_box_price: e.target.value })}
                  placeholder={boxEnabled ? '留空则无' : '先填箱容'} />
              </div>
            </div>
          </div>

          {/* 促销设置：手动开关，不设时间范围。最终结算价永远取"售价/VIP价/促销价"最低的那个，
              就算促销价设置失误比原价还高，也不会出现"开了促销反而更贵"的情况 */}
          <div className="border border-orange-200 rounded-xl p-4 bg-orange-50">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="text-lg">🔥</span>
                <h4 className="font-semibold text-gray-800">促销设置</h4>
                <span className="text-xs text-gray-500">（手动开关，不设时间范围）</span>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={formData.is_promo_active}
                  onChange={e => setFormData({ ...formData, is_promo_active: e.target.checked })}
                  className="w-4 h-4 rounded" />
                <span className={`text-sm font-medium ${formData.is_promo_active ? 'text-orange-600' : 'text-gray-500'}`}>
                  {formData.is_promo_active ? '促销中' : '未开启'}
                </span>
              </label>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>促销价 (USD)</label>
                <input type="number" step="0.01" min="0" className={inputClass}
                  value={formData.promo_price}
                  onChange={e => setFormData({ ...formData, promo_price: e.target.value })}
                  placeholder="留空则不参与比价" />
              </div>
              <div>
                <label className={labelClass}>促销箱价 (USD)</label>
                <input type="number" step="0.01" min="0" className={inputClass}
                  value={formData.promo_box_price}
                  disabled={!boxEnabled}
                  onChange={e => setFormData({ ...formData, promo_box_price: e.target.value })}
                  placeholder={boxEnabled ? '留空则不参与比价' : '先填箱容'} />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-3">
              💡 最终结算价会自动取"售价 / VIP价 / 促销价"三者中最低的那个，客人不用是VIP也能享受促销价
            </p>
          </div>

          {/* 图片 */}
          <div>
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">商品图片</h3>
            <div className="flex items-start gap-4">
              <div
                className={`w-24 h-24 border-2 border-dashed border-gray-300 rounded-xl flex items-center justify-center overflow-hidden bg-gray-50 flex-shrink-0 ${
                  imagePreview ? 'cursor-zoom-in hover:ring-2 hover:ring-blue-400 transition-all' : ''
                }`}
                onClick={handleZoomOpen}
                title={imagePreview ? '点击查看大图' : ''}
              >
                {imagePreview ? (
                  <img src={imagePreview} alt="preview" className="w-full h-full object-cover"
                    onError={() => setUrlError(true)} />
                ) : (
                  <ImageIcon className="w-8 h-8 text-gray-400" />
                )}
              </div>
              <div className="flex-1 space-y-2">
                <div className="flex items-center gap-3 flex-wrap">
                  <label className="cursor-pointer flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                    <Upload className="w-4 h-4" />
                    {uploading ? '上传中...' : '选择图片'}
                    <input type="file" accept="image/*" className="hidden"
                      onChange={handleImageChange} disabled={uploading} />
                  </label>
                  <span className="text-xs text-gray-400">或</span>
                  <button type="button"
                    onClick={() => setShowUrlInput(!showUrlInput)}
                    className="text-sm text-blue-600 hover:underline">
                    輸入圖片鏈接
                  </button>
                  {imagePreview && (
                    <button type="button" onClick={handleRemoveImage}
                      className="text-red-400 hover:text-red-600 text-sm ml-auto">
                      删除图片
                    </button>
                  )}
                </div>
                {showUrlInput && (
                  <div>
                    <input type="text" value={imageUrlInput}
                      onChange={e => { setImageUrlInput(e.target.value); setUrlError(false); }}
                      onBlur={handleUrlConfirm}
                      placeholder="貼上圖片URL，例如 https://example.com/product.jpg"
                      className={inputClass} />
                    {urlError && (
                      <p className="text-xs text-red-500 mt-1">圖片鏈接無效或無法加載，請檢查後重試</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">显示设置</h3>
            <div className="grid grid-cols-2 gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={formData.show_in_pos}
                  onChange={e => setFormData({ ...formData, show_in_pos: e.target.checked })}
                  className="w-4 h-4 rounded" />
                <span className="text-sm text-gray-700">在收银台显示</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={formData.show_in_h5}
                  onChange={e => setFormData({ ...formData, show_in_h5: e.target.checked })}
                  className="w-4 h-4 rounded" />
                <span className="text-sm text-gray-700">在H5商城显示</span>
              </label>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-3">
              <div>
                <label className={labelClass}>排序权重</label>
                <input type="number" className={inputClass}
                  value={formData.sort_order}
                  onChange={e => setFormData({ ...formData, sort_order: e.target.value })} />
              </div>
              <div>
                <label className={labelClass}>状态</label>
                <select className={inputClass} value={formData.status}
                  onChange={e => setFormData({ ...formData, status: e.target.value })}>
                  <option value="active">上架</option>
                  <option value="inactive">下架</option>
                </select>
              </div>
            </div>
          </div>

          <div className="border border-blue-200 rounded-xl p-4 bg-blue-50">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-lg">🏷️</span>
              <h4 className="font-semibold text-gray-800">多规格设置</h4>
              <span className="text-xs text-gray-500">（不设置则为单规格商品，随商品一起保存）</span>
            </div>
            <ProductSpecEditor
              specs={specs}
              skus={skus}
              mainPrice={formData.sale_price}
              mainVipPrice={formData.vip_price}
              onChange={(newSpecs, newSkus) => {
                setSpecs(newSpecs);
                setSkus(newSkus);
              }}
            />
          </div>

        </form>

        <div className="flex justify-end gap-3 p-6 border-t flex-shrink-0">
          <button type="button" onClick={onClose}
            className="px-6 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
            取消
          </button>
          <button type="button" onClick={handleSubmit}
            disabled={loading || uploading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
            {loading ? '保存中...' : uploading ? '上传图片中...' : '保存商品'}
          </button>
        </div>
      </div>

      {/* 主图放大预览遮罩层 */}
      {zoomImage && (
        <div
          className="fixed inset-0 z-[60] bg-black/75 flex items-center justify-center p-4"
          onClick={() => setZoomImage(null)}
        >
          <button
            type="button"
            onClick={() => setZoomImage(null)}
            className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"
            title="关闭"
          >
            <X className="w-8 h-8" />
          </button>
          <img
            src={zoomImage.src}
            alt="商品图片"
            className="max-w-full max-h-full rounded-lg shadow-2xl object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default ProductDialog;